/**
 * Step five — the signup invoice, previewed, and how it gets paid.
 *
 * Two things, and the second is a gap rather than a feature.
 *
 * **The preview is `billing_run$` with `dry_run: true`.** It runs the six steps of a
 * real run and rolls them back, so the preview cannot disagree with the invoice —
 * and it mints no reference, because a reference taken inside a transaction that is
 * rolled back is a gap in a sequence Wise matching needs to be gapless. It is behind
 * a button rather than fetched on arrival for a reason worth stating: `billing_run`
 * is a `money`-scoped action and the registry requires an `idempotency_key` on every
 * one, which `useStudioAction` generates and `useStudioRead` does not. A dry run is a
 * `POST` that the API treats as a write; asking for it is therefore a click.
 * `.docs/20` §E's money page has the same button for the same reason.
 *
 * **There is no action behind the payment-method link.** `charge.startSetup` exists in
 * `modules/billing/charge.ts` and no `defineAction` calls it, so a studio member
 * cannot mint a hosted setup link from here; what they can do is tell the client
 * where it is. The report asks the lead for `payments_start_setup` and this step is
 * the sentence until there is one.
 *
 * The retainer is re-read rather than carried: `?step=` and `?client=` are the whole
 * of the wizard's state, so a reload on this step has a slug and nothing else.
 */

import {
  DryRunInvoiceSchema,
  RetainerSchema,
  firstFullMonth,
  type DryRunInvoiceView,
} from '@fareast/shared'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction, useStudioRead } from '../../../api/studio.js'
import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Card, CardBody, CardHead, CardTitle } from '../../../components/portal/Card.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Stack } from '../../../components/portal/Layout.js'
import { Notice } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import { B, Muted, Num, Prose, Ref, Small } from '../../../components/portal/Text.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { useFormat } from '../../../format/useFormat.js'

/** As much of `billing_run`'s answer as this step reads. */
const RunAnswerSchema = z.object({ dry_run: DryRunInvoiceSchema.nullable() })

export type PaymentStepProps = {
  readonly client: string
  /** Back to the package step, for an account that somehow has no retainer. */
  readonly onBack: () => void
}

export function PaymentStep({ client, onBack }: PaymentStepProps) {
  const format = useFormat()
  const retainer = useStudioRead('retainers_get', { client }, { inline: true })
  const preview = useStudioAction('billing_run')
  const [dryRun, setDryRun] = useState<DryRunInvoiceView | null>(null)

  /*
   * The refusal first, then the wait.
   *
   * `retainers_get` **throws** for a client with no live retainer — "That client is
   * not on a retainer. retainers_start is how one begins" — rather than answering
   * null, so `inline: true` keeps that refusal on this page instead of throwing it at
   * the route boundary, and its own sentence is the one worth printing.
   */
  if (retainer.isError) {
    return (
      <Stack gap="loose">
        <Notice need role="alert">
          {messageOf(retainer.error)}
        </Notice>
        <ButtonRow>
          <Button onClick={onBack} variant="primary">
            Back to the package
          </Button>
        </ButtonRow>
      </Stack>
    )
  }

  if (retainer.isPending) return <Empty>Reading the retainer…</Empty>

  const row = RetainerSchema.parse(retainer.data)
  const period = firstFullMonth(row.started_on)
  const review = reviewOf(preview.data)

  const run = async () => {
    try {
      const output = await preview.mutateAsync({ client, period, kind: 'signup', dry_run: true })
      setDryRun(reviewOf(output) === null ? RunAnswerSchema.parse(output).dry_run : null)
    } catch {
      /* Rendered from `preview.error` below. */
    }
  }

  return (
    <Stack gap="loose">
      <Prose>
        <B>{row.name}</B> is live at {format.money(row.monthly_amount)} a month from{' '}
        {format.day(row.started_on)}. The signup invoice carries the rest of that month, prorated by
        calendar days, plus the whole of {format.monthLong(period)} — it is queued already and the
        worker issues it. Preview it here to see the lines before it goes.
      </Prose>

      <ButtonRow>
        <Button
          disabled={preview.isPending}
          onClick={() => {
            void run()
          }}
          variant="primary"
        >
          {preview.isPending ? 'Working it out…' : 'Preview the signup invoice'}
        </Button>
      </ButtonRow>

      {review === null ? null : <NeedsReviewNotice review={review} />}
      {preview.isError ? (
        <Notice need role="alert">
          {messageOf(preview.error)}
        </Notice>
      ) : null}

      {dryRun === null ? null : dryRun.would_issue ? (
        <Card>
          <CardHead rule>
            <CardTitle>
              {format.monthLong(dryRun.period)} — {format.money(dryRun.total)}
            </CardTitle>
          </CardHead>
          <CardBody scrollX tight>
            <Table>
              <THead>
                <Tr>
                  <Th>Line</Th>
                  <Th right>Qty</Th>
                  <Th right>Each</Th>
                  <Th right>Amount</Th>
                </Tr>
              </THead>
              <TBody>
                {dryRun.lines.map((line) => (
                  <Tr key={line.seq}>
                    <Td>
                      {line.description}
                      {line.ref === null ? null : (
                        <Small>
                          {' '}
                          <Muted>
                            <Ref value={line.ref} />
                          </Muted>
                        </Small>
                      )}
                    </Td>
                    <Td right num>
                      <Num value={line.qty} />
                    </Td>
                    <Td right num>
                      <Num value={line.unit_amount} />
                    </Td>
                    <Td right num>
                      <Num value={line.amount} />
                    </Td>
                  </Tr>
                ))}
                <Tr variant="total">
                  <Td colSpan={3}>Total</Td>
                  <Td right num>
                    <Num lg value={dryRun.total} />
                  </Td>
                </Tr>
              </TBody>
            </Table>
          </CardBody>
          <CardBody>
            <Small>
              <Muted>
                Computed and rolled back, so it has no reference of its own. The invoice the run
                writes will carry the next number in the sequence.
              </Muted>
            </Small>
          </CardBody>
        </Card>
      ) : (
        <Empty>Nothing to bill for {format.monthLong(dryRun.period)}.</Empty>
      )}

      <Card>
        <CardHead>
          <CardTitle>Getting paid</CardTitle>
        </CardHead>
        <CardBody>
          <Prose>
            Send the client their portal link. They add a card or a bank mandate themselves, on{' '}
            <B>Settings → Payment method</B>, and the invoice above is charged to whatever is on
            file when it is issued. There is no studio tool that mints a setup link for them —
            asking somebody for their card details over email is exactly what the hosted page exists
            to avoid.
          </Prose>
          <Prose>
            An invoice with no method on file is still issued and still due; the studio is told, and
            the client can pay it from the portal.
          </Prose>
        </CardBody>
      </Card>

      <ButtonRow>
        <Button asChild variant="primary">
          <Link params={{ slug: client }} to="/studio/clients/$slug">
            Open the account
          </Link>
        </Button>
        <Button asChild variant="ghost">
          <Link to="/studio/clients">Back to the book</Link>
        </Button>
      </ButtonRow>
    </Stack>
  )
}
